import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import useAPI from '../../../../../hooks/useAPI';
import SUTextField from '../../Components/SUTextField';
import NodeEditorChrome from '../../Components/NodeEditorChrome';
import TriggerTypeField from '../../Components/TriggerTypeField';
import useApprovedTemplates from '../../Components/useApprovedTemplates';
import useNodeEditor from '../../Components/useNodeEditor';
import { renderVariableChips } from '../../Components/VariableChips';
import {
  FieldLabel,
  FormSection,
  ChipButton,
  ListRow,
  EmptyHint,
  InfoCallout,
} from '../../Components/NodeFormKit';
import { getFlowsWildcardError } from '../../Components/flowTriggerRules';
import styledComponent from 'styled-components';
import Select from 'react-select';

import { FaCheckCircle } from 'react-icons/fa';
import { BiSolidError } from 'react-icons/bi';
import { FiSettings } from 'react-icons/fi';
import { FaArrowRightArrowLeft } from 'react-icons/fa6';

const CONDITIONS_NODE_COLOR = '#E5253D';

const QueryApi = ({
  selectedNode,
  handleSelectedNode,
  removeNode,
  nodes,
  edges,
  editFlow,
  updateBot,
  truncateText,
  customStyles,
  customStylesDark,
  isDark,
  watchSelectedNode,
  handleShowConditionsModal,
  handleAnchorEl,
  onMouseDown,
  isDragging,
  isExpanded,
  setIsExpanded,
}) => {
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    setError,
  } = useForm({
    user_response: null,
  });

  const { templates, loading: loadingTemplates } = useApprovedTemplates();

  const { urlAPI_2, fetchWithAuth } = useAPI();

  // ------> Selects, Radios, Checkbox
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [errorConditions, setErrorConditions] = useState(false);
  const { handleInputChange, loadData, saveNode, getIsDirty } = useNodeEditor({
    selectedNode,
    handleSelectedNode,
    nodes,
    edges,
    editFlow,
    updateBot,
    reset,
    getValues,
  });

  // FUNCTIONS



  //# Function para guardar los datos
  const saveData = (values) => {
    if (!selectedNode.data.conditions?.length) {
      setErrorConditions(true);
      return;
    }
    const flowsWildcardError = getFlowsWildcardError(
      selectedNode.data?.trigger_type,
      values.user_response,
      selectedNode.data?.flow_template_id
    );
    if (flowsWildcardError) {
      setError('user_response', { type: 'manual', message: flowsWildcardError });
      return;
    }

    setLoadingUpdate(true);

    const data = {
      title: values.title,
      user_response: values.user_response,
      control_message: values.control_message,
      trigger_type: selectedNode.data?.trigger_type || 'message',
    };

    saveNode(data, () => {
      setLoadingUpdate(false);
    });
  };

  //# Function para obtner data
  const getData = () => {
    let objectData = {
      title: selectedNode.data?.title ?? null,
      user_response: selectedNode.data?.user_response ?? null,
      control_message: selectedNode.data?.control_message ?? null,
      trigger_type: selectedNode.data?.trigger_type ?? 'message',
    };
    loadData(objectData);
  };

  useEffect(() => {
    getData();
  }, [watchSelectedNode]);

  useEffect(() => {
    if (selectedNode.data?.conditions?.length) setErrorConditions(false);
  }, [selectedNode.data?.conditions?.length]);

  return (
    <NodeEditorChrome
      icon={FaArrowRightArrowLeft}
      color={CONDITIONS_NODE_COLOR}
      typeName="Condiciones"
      title={truncateText(selectedNode.data.title)}
      onRemove={() => removeNode(selectedNode.id)}
      onMouseDown={onMouseDown}
      isDragging={isDragging}
      onClose={() => handleSelectedNode(null)}
      formId="conditionsForm"
      saving={loadingUpdate}
      isDark={isDark}
      getIsDirty={getIsDirty}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    >
        <form id="conditionsForm" onSubmit={handleSubmit(saveData)} style={{ width: '100%' }}>
          <div>
            <div className="col p-0">
              <SUTextField
                id="title"
                name="title"
                label="Título:"
                type="text"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('title', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
            </div>
            {!selectedNode.data?.first && (<>
            <div className="col p-0 mt-4">
              <TriggerTypeField
                value={selectedNode.data?.trigger_type}
                handleInputChange={handleInputChange}
                isDark={isDark}
                customStyles={customStyles}
                customStylesDark={customStylesDark}
              />
            </div>
            {selectedNode.data?.trigger_type === 'flows' && (
              <div className="col p-0 mt-4">
                <FieldLabel htmlFor="flow_template_id">
                  Plantilla esperada (opcional):
                </FieldLabel>
                <div className="su-control-group d-flex">
                  <Select
                    styles={isDark == 'true' ? customStylesDark : customStyles}
                    className="form-control basic-multi-select"
                    placeholder={loadingTemplates ? 'Cargando plantillas...' : 'Seleccione una plantilla'}
                    name="flow_template_id"
                    value={
                      templates.find(
                        (templateOption) =>
                          String(templateOption.value) === String(selectedNode.data?.flow_template_id || '')
                      ) || null
                    }
                    onChange={(option) => {
                      handleInputChange('flow_template_id', option ? String(option.value) : null);
                      handleInputChange('flow_template_name', option ? option.label : null);
                    }}
                    options={templates}
                    isClearable
                    isLoading={loadingTemplates}
                    classNamePrefix="select"
                    noOptionsMessage={() => 'Sin opciones'}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                  />
                </div>
                <InfoCallout>
                  Si seleccionas una plantilla, este nodo tipo flujo solo se
                  activará cuando el usuario responda a esa plantilla. Con * (cualquier respuesta) la plantilla es obligatoria y el nodo solo se activa dentro de las 24 h siguientes a su envío.
                </InfoCallout>
              </div>
            )}
            <div className="col p-0 mt-4">
              <SUTextField
                id="user_response"
                name="user_response"
                label="Respuesta del cliente:"
                hint="Lo que debe llegar para activar este nodo: el texto que escribe el cliente, el título de un botón, o * para cualquier respuesta. Para varios, separalos con  /  ."
                type="textArea"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('user_response', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
            </div>
            </>)}
            <div className="col p-0 mt-4">
              <FieldLabel>Mensaje de control (Opcional):</FieldLabel>
              {selectedNode.data?.control_message && typeof selectedNode.data.control_message === 'string' && selectedNode.data.control_message.startsWith('http') ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', borderRadius: 8, padding: 10, minHeight: 90 }}>
                  <img src={selectedNode.data.control_message} alt="Sticker" style={{ maxHeight: 64, maxWidth: 64, borderRadius: 8 }} />
                  <button
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#E5253D',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: 18,
                      marginLeft: 8,
                    }}
                    title="Eliminar sticker"
                    onClick={() => {
                      setValue('control_message', '', { shouldDirty: true });
                      handleSelectedNode((prevInputValue) => ({
                        ...prevInputValue,
                        data: {
                          ...prevInputValue.data,
                          control_message: '',
                        },
                      }));
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <SUTextField
                  id="control_message"
                  name="control_message"
                  label=""
                  type="textArea"
                  showEmoji
                  showSticker
                  selectedNode={selectedNode}
                  getValues={getValues}
                  setValue={setValue}
                  errors={errors}
                  {...register('control_message', {
                    pattern: {
                      value: /\S+/, 
                      message: 'No puede ser solo espacios en blanco',
                    },
                  })}
                />
              )}
            </div>
            <FormSection
              title="Condiciones"
              style={{ margin: '16px 0', maxHeight: '400px', overflowY: 'auto' }}
            >
              {selectedNode.data?.conditions?.length ? (
                selectedNode.data.conditions.map((condition, index) => {
                  const hideValues = [
                    'status',
                    'whatsapp_message',
                    'window_24h',
                  ].includes(condition.rule.operatorType);

                  return (
                    <ListRow key={index}>
                      {!hideValues && (
                        <p style={{ margin: 0 }}>
                          Valor: {renderVariableChips(condition?.rule?.value1)}
                        </p>
                      )}
                      <p style={{ margin: 0 }}>
                        Operador: {condition?.rule?.operator?.label}
                      </p>
                      {condition?.rule?.value2 && !hideValues && (
                        <p style={{ margin: 0 }}>
                          Valor: {renderVariableChips(condition?.rule?.value2)}
                        </p>
                      )}
                    </ListRow>
                  );
                })
              ) : (
                <EmptyHint>Aún no hay condiciones.</EmptyHint>
              )}

              <ChipButton
                icon={FiSettings}
                onClick={(event) => {
                  handleAnchorEl(event.currentTarget);
                  handleShowConditionsModal(true);
                }}
              >
                Gestionar condiciones
              </ChipButton>
              {errorConditions && (
                <Alert>* Agrega al menos una condición para guardar.</Alert>
              )}
            </FormSection>
            <FormSection title="Estados de la Solicitud" style={{ marginTop: '8px' }}>
              <div className="col p-0">
                <FieldLabel htmlFor="succes">Solicitud exitosa:</FieldLabel>
                <div className="d-flex align-items-center">
                  <FaCheckCircle
                    className="mr-1"
                    style={{
                      fontSize: '22px',
                      color: '#2fae4e',
                    }}
                  />
                  <input
                    className="form-control"
                    id="succes"
                    name="succes"
                    type="text"
                    style={{
                      width: '100%',
                      height: '35px',
                    }}
                    value={selectedNode.data.succes || ''}
                    onChange={(e) => {
                      handleSelectedNode((prevInputValue) => {
                        return {
                          ...prevInputValue,
                          data: {
                            ...prevInputValue.data,
                            succes: e.target.value,
                          },
                        };
                      });
                    }}
                    readOnly
                  />
                </div>
              </div>
              <div className="col p-0 mt-2">
                <FieldLabel htmlFor="failed">Solicitud fallida:</FieldLabel>
                <div className="d-flex align-items-center">
                  <BiSolidError
                    className="mr-1"
                    style={{
                      fontSize: '22px',
                      color: '#FF2242',
                    }}
                  />
                  <input
                    className="form-control"
                    id="failed"
                    name="failed"
                    type="text"
                    style={{
                      width: '100%',
                      height: '35px',
                    }}
                    value={selectedNode.data.failed || ''}
                    onChange={(e) => {
                      handleSelectedNode((prevInputValue) => {
                        return {
                          ...prevInputValue,
                          data: {
                            ...prevInputValue.data,
                            failed: e.target.value,
                          },
                        };
                      });
                    }}
                    readOnly
                  />
                </div>
              </div>
            </FormSection>
          </div>
        </form>
    </NodeEditorChrome>
  );
};

export default QueryApi;

const Alert = styledComponent.span`
  color: red;
  display: block;
  width: 100%;
  // width: 200px;
`;
